import * as THREE from 'three'

import { AIRCRAFT } from '../config/constants.js'
import { clamp, wrapDeg360 } from '../util/units.js'

/**
 * Round-dial instruments, drawn into canvases and used as textures.
 *
 * Canvas is the right tool here rather than geometry: a needle is one rotated
 * line, and redrawing five 256px dials twenty times a second costs less than
 * animating five meshes. Redraw rate is deliberately decoupled from the render
 * loop — instruments that update at 20 Hz look completely normal and free up
 * the frame budget for the sea.
 */

const SIZE = 256

function baseDial(ctx, label) {
  ctx.clearRect(0, 0, SIZE, SIZE)

  // Bezel.
  ctx.fillStyle = '#101315'
  ctx.beginPath()
  ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2 - 2, 0, Math.PI * 2)
  ctx.fill()

  ctx.strokeStyle = '#2c3338'
  ctx.lineWidth = 6
  ctx.beginPath()
  ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2 - 6, 0, Math.PI * 2)
  ctx.stroke()

  ctx.fillStyle = '#5d6b73'
  ctx.font = '600 15px ui-monospace, monospace'
  ctx.textAlign = 'center'
  ctx.fillText(label, SIZE / 2, SIZE - 26)
}

function ticks(ctx, count, majorEvery, r0, r1, startDeg = -120, sweepDeg = 240) {
  for (let i = 0; i <= count; i++) {
    const a = ((startDeg + (sweepDeg * i) / count) * Math.PI) / 180
    const major = i % majorEvery === 0
    ctx.strokeStyle = major ? '#d8e6ec' : '#78868d'
    ctx.lineWidth = major ? 3.5 : 2
    const rr0 = major ? r0 : r0 + 8
    ctx.beginPath()
    ctx.moveTo(SIZE / 2 + Math.sin(a) * rr0, SIZE / 2 - Math.cos(a) * rr0)
    ctx.lineTo(SIZE / 2 + Math.sin(a) * r1, SIZE / 2 - Math.cos(a) * r1)
    ctx.stroke()
  }
}

function needle(ctx, angleDeg, length, width, color, tailLength = 18) {
  const a = (angleDeg * Math.PI) / 180
  ctx.save()
  ctx.translate(SIZE / 2, SIZE / 2)
  ctx.rotate(a)
  ctx.fillStyle = color
  ctx.beginPath()
  ctx.moveTo(-width / 2, tailLength)
  ctx.lineTo(-width * 0.32, -length)
  ctx.lineTo(width * 0.32, -length)
  ctx.lineTo(width / 2, tailLength)
  ctx.closePath()
  ctx.fill()
  ctx.restore()

  ctx.fillStyle = '#c9d6dc'
  ctx.beginPath()
  ctx.arc(SIZE / 2, SIZE / 2, 7, 0, Math.PI * 2)
  ctx.fill()
}

function digits(ctx, text, y, size = 26, color = '#c9f2dc') {
  ctx.fillStyle = '#05080a'
  const w = size * text.length * 0.68
  ctx.fillRect(SIZE / 2 - w / 2, y - size * 0.72, w, size * 1.05)
  ctx.strokeStyle = '#39464d'
  ctx.lineWidth = 2
  ctx.strokeRect(SIZE / 2 - w / 2, y - size * 0.72, w, size * 1.05)
  ctx.fillStyle = color
  ctx.font = `700 ${size}px ui-monospace, monospace`
  ctx.textAlign = 'center'
  ctx.fillText(text, SIZE / 2, y)
}

export class Gauge {
  constructor(label, drawFn) {
    this.canvas = document.createElement('canvas')
    this.canvas.width = this.canvas.height = SIZE
    this.ctx = this.canvas.getContext('2d')
    this.label = label
    this.drawFn = drawFn

    this.texture = new THREE.CanvasTexture(this.canvas)
    this.texture.colorSpace = THREE.SRGBColorSpace
    this.texture.anisotropy = 4

    this.material = new THREE.MeshBasicMaterial({
      map: this.texture,
      transparent: true,
      toneMapped: false,
    })

    this.mesh = new THREE.Mesh(new THREE.CircleGeometry(0.5, 32), this.material)
    this.draw({})
  }

  draw(state) {
    baseDial(this.ctx, this.label)
    this.drawFn(this.ctx, state)
    this.texture.needsUpdate = true
  }

  dispose() {
    this.mesh.geometry.dispose()
    this.material.dispose()
    this.texture.dispose()
  }
}

/* ===================================================================== *
 *  The five dials that matter in the pattern                            *
 * ===================================================================== */

export function airspeedIndicator() {
  return new Gauge('KIAS', (ctx, s) => {
    ticks(ctx, 12, 2, 88, 108)
    const kias = clamp(s.kcas ?? 0, 0, 700)
    // 0-700 kts over 240 degrees.
    needle(ctx, -120 + (kias / 700) * 240, 92, 11, '#eef4f6')
    digits(ctx, String(Math.round(kias)), SIZE / 2 + 52, 24)
  })
}

export function altimeter() {
  return new Gauge('ALT x100 FT', (ctx, s) => {
    ticks(ctx, 10, 1, 88, 108, -180, 360)
    const alt = s.altFt ?? 0
    const hundreds = (alt / 1000) % 1
    needle(ctx, hundreds * 360, 92, 10, '#eef4f6')
    needle(ctx, ((alt / 10000) % 1) * 360, 58, 15, '#9fb4bd', 12)
    digits(ctx, String(Math.round(alt)), SIZE / 2 + 52, 22)
  })
}

export function verticalSpeed() {
  return new Gauge('VVI x1000 FPM', (ctx, s) => {
    ticks(ctx, 12, 3, 88, 108, -150, 300)
    const vs = clamp((s.vsFpm ?? 0) / 6000, -1, 1)
    needle(ctx, vs * 150, 92, 10, '#ffd479')
    digits(ctx, String(Math.round(s.vsFpm ?? 0)), SIZE / 2 + 52, 20)
  })
}

/**
 * The AoA gauge. Not a generic dial — the on-speed band is painted on, because
 * that band is the single most important thing on the panel in the groove.
 */
export function aoaIndicator() {
  return new Gauge('AOA UNITS', (ctx, s) => {
    // On-speed band, 14.4 to 15.6 units.
    const toAngle = (u) => -120 + (clamp(u, 0, 30) / 30) * 240
    ctx.strokeStyle = 'rgba(61,255,154,0.55)'
    ctx.lineWidth = 14
    ctx.beginPath()
    ctx.arc(
      SIZE / 2,
      SIZE / 2,
      82,
      ((toAngle(AIRCRAFT.ONSPEED_AOA_UNITS - AIRCRAFT.AOA_DONUT_BAND) - 90) * Math.PI) / 180,
      ((toAngle(AIRCRAFT.ONSPEED_AOA_UNITS + AIRCRAFT.AOA_DONUT_BAND) - 90) * Math.PI) / 180,
    )
    ctx.stroke()

    ticks(ctx, 15, 5, 88, 108)
    const u = s.aoaUnits ?? 0
    needle(ctx, toAngle(u), 92, 10, '#ffb02e')
    digits(ctx, (s.aoaUnits ?? 0).toFixed(1), SIZE / 2 + 52, 22, '#ffd79a')
  })
}

/** Attitude director indicator. */
export function attitudeIndicator() {
  return new Gauge('ADI', (ctx, s) => {
    const cx = SIZE / 2
    const cy = SIZE / 2
    const r = 96

    ctx.save()
    ctx.beginPath()
    ctx.arc(cx, cy, r, 0, Math.PI * 2)
    ctx.clip()

    const pitch = s.pitchDeg ?? 0
    const roll = s.rollDeg ?? 0
    const pxPerDeg = 3.2

    ctx.translate(cx, cy)
    ctx.rotate((-roll * Math.PI) / 180)
    ctx.translate(0, pitch * pxPerDeg)

    ctx.fillStyle = '#2f7fb8'
    ctx.fillRect(-260, -400, 520, 400)
    ctx.fillStyle = '#7a5a34'
    ctx.fillRect(-260, 0, 520, 400)

    ctx.strokeStyle = '#f0f4f6'
    ctx.lineWidth = 3
    ctx.beginPath()
    ctx.moveTo(-200, 0)
    ctx.lineTo(200, 0)
    ctx.stroke()

    ctx.lineWidth = 2
    ctx.font = '600 12px ui-monospace, monospace'
    ctx.fillStyle = '#e4eaee'
    ctx.textAlign = 'center'
    for (let p = -60; p <= 60; p += 10) {
      if (p === 0) continue
      const y = -p * pxPerDeg
      const w = p % 20 === 0 ? 52 : 28
      ctx.beginPath()
      ctx.moveTo(-w, y)
      ctx.lineTo(w, y)
      ctx.stroke()
      if (p % 20 === 0) ctx.fillText(String(Math.abs(p)), w + 16, y + 4)
    }
    ctx.restore()

    // Fixed aircraft symbol.
    ctx.strokeStyle = '#ffcf4d'
    ctx.lineWidth = 4
    ctx.beginPath()
    ctx.moveTo(cx - 46, cy)
    ctx.lineTo(cx - 14, cy)
    ctx.moveTo(cx + 14, cy)
    ctx.lineTo(cx + 46, cy)
    ctx.moveTo(cx, cy - 5)
    ctx.lineTo(cx, cy + 5)
    ctx.stroke()

    // Bank pointer.
    ctx.fillStyle = '#e8eef1'
    ctx.beginPath()
    ctx.moveTo(cx, cy - r + 4)
    ctx.lineTo(cx - 8, cy - r + 20)
    ctx.lineTo(cx + 8, cy - r + 20)
    ctx.closePath()
    ctx.fill()
  })
}

/** Horizontal situation: heading card plus the ship's bearing. */
export function headingIndicator() {
  return new Gauge('HDG', (ctx, s) => {
    const cx = SIZE / 2
    const cy = SIZE / 2
    const hdg = wrapDeg360(s.headingDeg ?? 0)

    ctx.save()
    ctx.translate(cx, cy)
    ctx.rotate((-hdg * Math.PI) / 180)
    for (let d = 0; d < 360; d += 10) {
      const a = (d * Math.PI) / 180
      const major = d % 30 === 0
      ctx.strokeStyle = major ? '#dce8ee' : '#7d8b92'
      ctx.lineWidth = major ? 3 : 1.6
      ctx.beginPath()
      ctx.moveTo(Math.sin(a) * (major ? 84 : 92), -Math.cos(a) * (major ? 84 : 92))
      ctx.lineTo(Math.sin(a) * 104, -Math.cos(a) * 104)
      ctx.stroke()
      if (major) {
        ctx.save()
        ctx.translate(Math.sin(a) * 68, -Math.cos(a) * 68)
        ctx.rotate(a)
        ctx.fillStyle = '#dce8ee'
        ctx.font = '600 15px ui-monospace, monospace'
        ctx.textAlign = 'center'
        ctx.fillText(d === 0 ? 'N' : String(d / 10), 0, 5)
        ctx.restore()
      }
    }

    // Bearing pointer to the ship.
    if (Number.isFinite(s.bearingToShipDeg)) {
      const a = (s.bearingToShipDeg * Math.PI) / 180
      ctx.strokeStyle = '#ffb02e'
      ctx.lineWidth = 4
      ctx.beginPath()
      ctx.moveTo(0, 0)
      ctx.lineTo(Math.sin(a) * 78, -Math.cos(a) * 78)
      ctx.stroke()
    }
    ctx.restore()

    // Lubber line.
    ctx.fillStyle = '#ffcf4d'
    ctx.beginPath()
    ctx.moveTo(cx, cy - 108)
    ctx.lineTo(cx - 9, cy - 92)
    ctx.lineTo(cx + 9, cy - 92)
    ctx.closePath()
    ctx.fill()

    digits(ctx, String(Math.round(hdg)).padStart(3, '0'), SIZE / 2 + 56, 22)
  })
}
